import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Building2, 
  Radio, 
  Zap, 
  MapPin, 
  RefreshCw,
  Users,
  Activity,
  Wifi,
  WifiOff,
  X,
  Navigation,
  Plug,
  CircleDot
} from 'lucide-react';
import { NativeButton } from '../components/NativeComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Custom marker icons based on status
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: 14px;
          font-weight: bold;
        ">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </div>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36]
  });
};

// Status colors
const STATUS_COLORS = {
  active: '#22c55e',      // Yeşil - Aktif istasyon var ve online
  warning: '#f59e0b',     // Sarı - İstasyon var ama offline
  inactive: '#6b7280',    // Gri - İstasyon yok
  noLocation: '#94a3b8'   // Açık gri - Konum yok
};

// Map center adjuster component
const MapCenterAdjuster = ({ sites }) => {
  const map = useMap();
  
  useEffect(() => {
    if (sites && sites.length > 0) {
      const validSites = sites.filter(s => s.latitude && s.longitude);
      if (validSites.length > 0) {
        const bounds = L.latLngBounds(validSites.map(s => [s.latitude, s.longitude]));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [sites, map]);
  
  return null;
};

// Account type labels
const ACCOUNT_TYPE_LABELS = {
  'apartman': 'Apartman',
  'site': 'Site',
  'plaza': 'Plaza',
  'fabrika': 'Fabrika',
  'avm': 'AVM',
  'otopark': 'Otopark',
  'otel': 'Otel',
  'hastane': 'Hastane',
  'universite': 'Üniversite',
  'belediye': 'Belediye'
};

const HaritaPage = () => {
  const [sites, setSites] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailSite, setDetailSite] = useState(null);

  // Open detail modal
  const openDetailModal = (site) => {
    setDetailSite(site);
    setShowDetailModal(true);
  };

  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailSite(null);
  };

  // Get stations for a specific site
  const getSiteStations = (siteId) => {
    return stations.filter(s => s.site_id === siteId);
  };

  // Get status label in Turkish
  const getStationStatusLabel = (status) => {
    const statusMap = {
      'available': 'Müsait',
      'charging': 'Şarj Ediyor',
      'offline': 'Çevrimdışı',
      'faulted': 'Arızalı',
      'preparing': 'Hazırlanıyor',
      'finishing': 'Tamamlanıyor',
      'reserved': 'Rezerveli',
      'unavailable': 'Kullanılamaz'
    };
    return statusMap[status] || status || 'Bilinmiyor';
  };

  // Get status color
  const getStatusColor = (status) => {
    const colorMap = {
      'available': 'text-green-500',
      'charging': 'text-blue-500',
      'offline': 'text-gray-500',
      'faulted': 'text-red-500',
      'preparing': 'text-yellow-500',
      'finishing': 'text-purple-500',
      'reserved': 'text-orange-500',
      'unavailable': 'text-gray-400'
    };
    return colorMap[status] || 'text-gray-500';
  };

  // Build full address string
  const buildFullAddress = (site) => {
    const parts = [];
    if (site.neighborhood) parts.push(site.neighborhood);
    if (site.street) parts.push(site.street);
    if (site.building_no) parts.push(`No: ${site.building_no}`);
    if (site.apartment_no) parts.push(`Daire: ${site.apartment_no}`);
    if (site.district) parts.push(site.district);
    if (site.province) parts.push(site.province);
    return parts.length > 0 ? parts.join(', ') : site.address || 'Adres belirtilmemiş';
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('hims_token');
      if (!token) {
        setError('Oturum bulunamadı');
        return;
      }

      // Fetch sites
      const sitesResponse = await fetch(`${API_URL}/api/sites`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!sitesResponse.ok) {
        throw new Error('Hesaplar yüklenemedi');
      }
      
      const sitesData = await sitesResponse.json();
      setSites(sitesData);

      // Fetch stations
      const stationsResponse = await fetch(`${API_URL}/api/stations`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (stationsResponse.ok) {
        const stationsData = await stationsResponse.json();
        setStations(stationsData);
      }

    } catch (err) {
      console.error('Harita verisi yüklenemedi:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate site statistics
  const getSiteStats = (siteId) => {
    const siteStations = stations.filter(s => s.site_id === siteId);
    const activeStations = siteStations.filter(s => s.status === 'available' || s.status === 'charging');
    const onlineDevices = siteStations.filter(s => s.is_online);
    
    return {
      totalStations: siteStations.length,
      activeStations: activeStations.length,
      onlineDevices: onlineDevices.length
    };
  };

  // Get marker color based on site status
  const getMarkerColor = (site) => {
    if (!site.latitude || !site.longitude) return STATUS_COLORS.noLocation;
    
    const stats = getSiteStats(site.id);
    
    if (stats.totalStations === 0) return STATUS_COLORS.inactive;
    if (stats.onlineDevices > 0) return STATUS_COLORS.active;
    return STATUS_COLORS.warning;
  };

  // Calculate summary statistics
  const summaryStats = {
    totalSites: sites.length,
    sitesWithLocation: sites.filter(s => s.latitude && s.longitude).length,
    totalStations: stations.length,
    activeStations: stations.filter(s => s.status === 'available' || s.status === 'charging').length,
    onlineDevices: stations.filter(s => s.is_online).length
  };

  // Sites with valid coordinates
  const sitesWithCoords = sites.filter(s => s.latitude && s.longitude);

  if (loading) {
    return (
      <div data-testid="harita-page" className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-slate-600 dark:text-slate-400">Harita yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="harita-page" style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <MapPin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">Hesap Haritası</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Tüm hesapların konumlarını görüntüleyin
            </p>
          </div>
        </div>
        
        <NativeButton onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Yenile
        </NativeButton>
      </div>

      {/* Summary Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
          <Building2 className="w-5 h-5 text-blue-500" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Toplam Hesap</p>
            <p className="text-lg font-bold text-slate-800 dark:text-white">{summaryStats.totalSites}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
          <MapPin className="w-5 h-5 text-green-500" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Konumlu Hesap</p>
            <p className="text-lg font-bold text-slate-800 dark:text-white">{summaryStats.sitesWithLocation}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
          <Zap className="w-5 h-5 text-yellow-500" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Toplam İstasyon</p>
            <p className="text-lg font-bold text-slate-800 dark:text-white">{summaryStats.totalStations}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
          <Activity className="w-5 h-5 text-emerald-500" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Aktif İstasyon</p>
            <p className="text-lg font-bold text-slate-800 dark:text-white">{summaryStats.activeStations}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
          <Wifi className="w-5 h-5 text-cyan-500" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Çevrimiçi Cihaz</p>
            <p className="text-lg font-bold text-slate-800 dark:text-white">{summaryStats.onlineDevices}</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Durum:</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS.active }}></div>
          <span className="text-xs text-slate-600 dark:text-slate-400">Aktif & Online</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS.warning }}></div>
          <span className="text-xs text-slate-600 dark:text-slate-400">İstasyon Var (Offline)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS.inactive }}></div>
          <span className="text-xs text-slate-600 dark:text-slate-400">İstasyon Yok</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="m-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Map Container */}
      <div style={{ flex: 1, minHeight: '400px', position: 'relative' }}>
        {sitesWithCoords.length === 0 ? (
          <div className="flex items-center justify-center h-full bg-slate-100 dark:bg-slate-900">
            <div className="text-center p-8">
              <MapPin className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
                Konumlu Hesap Bulunamadı
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-500 max-w-md">
                Haritada gösterilecek konumlu hesap bulunmuyor. 
                Hesaplara konum eklemek için Hesaplar sayfasında "Konumu Al" butonunu kullanın.
              </p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={[39.0, 35.0]} // Türkiye merkezi
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapCenterAdjuster sites={sitesWithCoords} />
            
            {sitesWithCoords.map(site => {
              const stats = getSiteStats(site.id);
              const markerColor = getMarkerColor(site);
              
              return (
                <Marker
                  key={site.id}
                  position={[site.latitude, site.longitude]}
                  icon={createCustomIcon(markerColor)}
                  eventHandlers={{
                    click: () => setSelectedSite(site)
                  }}
                >
                  <Popup>
                    <div className="min-w-[220px]" style={{ fontFamily: 'inherit' }}>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-300" style={{ backgroundColor: '#3b82f6', margin: '-12px -12px 12px -12px', padding: '12px', borderRadius: '8px 8px 0 0' }}>
                        <Building2 className="w-5 h-5 text-white" />
                        <h3 style={{ fontWeight: 'bold', fontSize: '15px', color: 'white', margin: 0 }}>{site.name}</h3>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span style={{ color: '#64748b', fontWeight: '500' }}>Hesap Tipi:</span>
                          <span style={{ fontWeight: '700', color: '#1e293b' }}>
                            {ACCOUNT_TYPE_LABELS[site.account_type] || site.account_type || 'Belirtilmemiş'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span style={{ color: '#64748b', fontWeight: '500' }}>Adres:</span>
                          <span style={{ fontWeight: '600', color: '#1e293b', textAlign: 'right', maxWidth: '150px' }}>
                            {site.district && site.province ? `${site.district}, ${site.province}` : site.address || 'Belirtilmemiş'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span style={{ color: '#64748b', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Zap className="w-3 h-3" /> İstasyon:
                          </span>
                          <span style={{ fontWeight: '700', color: '#1e293b' }}>
                            {stats.totalStations > 0 ? `${stats.activeStations} aktif / ${stats.totalStations} toplam` : 'Yok'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span style={{ color: '#64748b', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {stats.onlineDevices > 0 ? (
                              <Wifi className="w-3 h-3 text-green-500" />
                            ) : (
                              <WifiOff className="w-3 h-3 text-slate-400" />
                            )}
                            Çevrimiçi:
                          </span>
                          <span style={{ fontWeight: '700', color: stats.onlineDevices > 0 ? '#16a34a' : '#475569' }}>
                            {stats.onlineDevices} cihaz
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-2 border-t border-slate-200">
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openDetailModal(site);
                          }}
                          style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            fontSize: '13px',
                            color: '#3b82f6',
                            fontWeight: '500',
                            textDecoration: 'none',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          Detayları Gör →
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>

      {/* Sites without location warning */}
      {sites.length > sitesWithCoords.length && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>
              <strong>{sites.length - sitesWithCoords.length}</strong> hesabın konumu belirlenmemiş. 
              Hesaplar sayfasında "Konumu Al" butonunu kullanarak konum ekleyebilirsiniz.
            </span>
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && detailSite && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={closeDetailModal}
        >
          <div 
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="flex items-center gap-3">
                <Building2 className="w-7 h-7" />
                <h2 className="text-xl font-bold">{detailSite.name}</h2>
              </div>
              <button 
                onClick={closeDetailModal}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Hesap Tipi */}
              <div className="mb-4">
                <p className="text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold mb-1">Hesap Tipi</p>
                <p className="text-base font-medium text-gray-800 dark:text-white">
                  {ACCOUNT_TYPE_LABELS[detailSite.account_type] || detailSite.account_type || 'Belirtilmemiş'}
                </p>
              </div>

              {/* Tam Adres */}
              <div className="mb-4">
                <p className="text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold mb-1">Tam Adres</p>
                <p className="text-base text-gray-700 dark:text-gray-200">
                  {buildFullAddress(detailSite)}
                </p>
              </div>

              {/* Konum Koordinatları */}
              <div className="mb-4">
                <p className="text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold mb-1">Konum Koordinatları</p>
                <div className="flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-blue-500" />
                  <p className="text-base text-gray-700 dark:text-gray-200">
                    {detailSite.latitude?.toFixed(6)}, {detailSite.longitude?.toFixed(6)}
                  </p>
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${detailSite.latitude},${detailSite.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-sm text-blue-500 hover:text-blue-600"
                >
                  <MapPin className="w-4 h-4" />
                  Google Maps'te Aç
                </a>
              </div>

              {/* İstasyon Listesi */}
              <div className="mb-4">
                <p className="text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold mb-2">
                  İstasyon Listesi ({getSiteStations(detailSite.id).length} istasyon)
                </p>
                
                {getSiteStations(detailSite.id).length === 0 ? (
                  <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg text-center">
                    <Plug className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Bu hesapta henüz istasyon bulunmuyor</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {getSiteStations(detailSite.id).map((station, idx) => (
                      <div 
                        key={station.id || idx}
                        className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-yellow-500" />
                            <span className="font-medium text-gray-800 dark:text-white">
                              {station.name || station.station_id || `İstasyon ${idx + 1}`}
                            </span>
                          </div>
                          <span className={`text-sm font-medium ${getStatusColor(station.status)}`}>
                            {getStationStatusLabel(station.status)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
                          <div className="flex items-center gap-1">
                            <CircleDot className="w-3 h-3" />
                            <span>{station.connector_count || station.connectors?.length || 1} konnektör</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {station.is_online ? (
                              <>
                                <Wifi className="w-3 h-3 text-green-500" />
                                <span className="text-green-600">Çevrimiçi</span>
                              </>
                            ) : (
                              <>
                                <WifiOff className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-500">Çevrimdışı</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Toplam Şarj Sayısı */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Toplam Şarj Sayısı</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {detailSite.total_sessions || detailSite.charging_sessions_count || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
              <button
                onClick={closeDetailModal}
                className="w-full py-2.5 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for markers */}
      <style>{`
        .custom-marker {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        }
        .leaflet-popup-content {
          margin: 12px;
        }
        .leaflet-container {
          font-family: inherit;
        }
      `}</style>
    </div>
  );
};

export default HaritaPage;
